import { DEBUG_CALLS } from '../../config';
import { logDebugMessage } from '../../util/debugConsole';

// Pinned to 2.7.5: the call service's LiveKit server uses the classic `/rtc/validate`
// signaling path. livekit-client 2.9+ switched to `/rtc/v1/...`, which this server 404s,
// so the unpinned/latest client fails to connect (WS closes 1006).
// Self-hosted: the pinned 2.7.5 UMD is vendored in `public/` and served from our own origin, so
// the app loads no third-party CDN script. To upgrade, replace `public/livekit-client.umd.min.js`.
const LIVEKIT_SCRIPT_SRC = '/livekit-client.umd.min.js';

let livekitLoadPromise: Promise<void> | undefined;

// Loads the vendored LiveKit UMD bundle from our own origin (idempotent). The 1:1 and group media
// layers both need `window.LivekitClient`, and both await this before reading it — a room that
// starts before the bundle has landed would otherwise find no client and silently connect no media.
export function loadLiveKitClient(): Promise<void> {
  if (livekitLoadPromise) return livekitLoadPromise;

  livekitLoadPromise = loadScript(LIVEKIT_SCRIPT_SRC).catch((err) => {
    livekitLoadPromise = undefined;
    logLiveKitScript('Failed to load LiveKit client', {
      error: err instanceof Error ? { name: err.name, message: err.message } : String(err),
    });
  });

  return livekitLoadPromise;
}

function loadScript(src: string) {
  return new Promise<void>((resolve, reject) => {
    const fail = () => reject(new Error(`Failed to load script: ${src}`));

    // A tag can already be in the document without having finished loading — resolving on its mere
    // presence would hand the caller a window where `LivekitClient` is still undefined. A failed tag
    // never reaches this branch: it is removed below, because its `error` has already fired and
    // would never fire again, leaving a retry waiting on it forever.
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
      if ((window as any).LivekitClient) {
        resolve();
      } else {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', fail, { once: true });
      }
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Drop the dead tag so the next attempt starts from a clean document. Left in place it would
      // be picked up as `existing` above and awaited for an event that has already fired — and since
      // the connect paths now await this promise, that turns one failed load into a call that hangs
      // on "connecting" forever, with `isConnectingRoom` stuck and every later call refused.
      script.remove();
      fail();
    };
    document.head.appendChild(script);
  });
}

function logLiveKitScript(message: string, data: Record<string, unknown> = {}) {
  if (!DEBUG_CALLS) return;
  logDebugMessage('warn', `[LiveKit][script] ${message}`, data);
}
